import { Feature, FeatureCollection, GeoJsonObject, Geometry, LineString, Point, Position } from "geojson";
import { LngLatBounds, Map as MaplibreMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, MapRef, NavigationControl, Source } from "react-map-gl/maplibre";
import { useCongTerritory } from "../../../api/territory";
import { extendBoundsWithGeometry, getMapBounds, isValidLatLng } from "../../../helpers/geo";
import { CongSettings } from "../../../types/scheduling/settings";
import { defaultSortOrder, Territory, TerritoryAddress } from "../../../types/scheduling/territory";
import {
  addressesGeoJSON,
  annotationTextField,
  hasAnnotationText,
  LabeledLayer,
  mapFontStack,
  tileProvider,
  tileStyle,
  transformMapboxRequest,
} from "./common";
import { HGContext } from "../../../helpers/globals";

const defaultGeoJSONPaint = { "fill-color": "rgba(0,128,256,0.2)" };
const TERRITORIES_FILL_LAYER_ID = "_territories-fill";
const addressLayerIds = ["addresses-with-text", "addresses-without-text", "dnc-with-text", "dnc-without-text"];

function MapWithGeoJsonInner(props: {
  territory?: Territory;
  settings: CongSettings;
  geojson?: GeoJsonObject;
  addresses?: TerritoryAddress[];
  labeledLayers?: LabeledLayer[];
  navigateToMap: (id: number) => void;
  onClick?: (p: Position) => void;
  restrictMarkerToBounds?: boolean;
  fullHeight?: boolean;
  hideZoomControl?: boolean;
  setMaplibreMap: (mlMap: MaplibreMap) => void;
  pubMode?: boolean;
  onMarkerClick?: (addressId: number) => void;
}) {
  const congTerritoryQuery = useCongTerritory({ enabled: !props.pubMode });
  const [userInteracted, setUserInteracted] = useState(false);
  const divStyle = props.fullHeight === true ? { height: "60vh" } : { width: 600, height: 400 };
  const provider = tileProvider(props.settings);
  const ctx = useContext(HGContext);

  const annotations: FeatureCollection | undefined = useMemo(
    () => (props.territory?.annotations ? JSON.parse(props.territory.annotations) : undefined),
    [props.territory?.annotations],
  );

  const mapRef = useRef<MapRef | null>(null);
  const mapRefCallback = useCallback(
    (ref: MapRef | null) => {
      if (ref !== null) {
        mapRef.current = ref;
        props.setMaplibreMap(ref.getMap());
        addMarkerImages(ref.getMap());
      }
    },
    [props],
  );

  const allAnnotations: FeatureCollection = useMemo(() => {
    const fc: FeatureCollection = {
      type: "FeatureCollection",
      features:
        annotations?.features.filter(
          (f): f is Feature<Point | LineString> =>
            (f.geometry.type === "Point" && hasAnnotationText(f)) || f.geometry.type === "LineString",
        ) || [],
    };
    return fc;
  }, [annotations]);

  const mapBounds = useMemo(() => {
    const addresses = addressesGeoJSON(props.addresses ?? []);
    const congTerr = congTerritoryQuery.data?.boundaries ? JSON.parse(congTerritoryQuery.data.boundaries) : undefined;

    return getMapBounds(props.geojson, annotations, addresses, congTerr, ctx.globals.cong?.jworg_location);
  }, [props.geojson, annotations, props.addresses, congTerritoryQuery.data, ctx.globals.cong?.jworg_location]);

  // Update map bounds when addresses populate, but not if user has manually interacted
  useEffect(() => {
    if (mapRef.current && mapBounds && !userInteracted) {
      const map = mapRef.current.getMap();
      const bounds = mapBounds instanceof LngLatBounds ? mapBounds : new LngLatBounds(mapBounds);
      map.fitBounds(bounds, {
        padding: { top: 30, bottom: 30, left: 30, right: 30 },
        maxZoom: 18,
        duration: 1000,
      });
    }
  }, [mapBounds, userInteracted]);

  const fontStack = mapFontStack(props.settings);
  const interactiveLayerIds = useMemo(() => {
    const ids = [...addressLayerIds];
    if (props.labeledLayers?.length) ids.push(TERRITORIES_FILL_LAYER_ID);
    return ids;
  }, [props.labeledLayers]);

  return (
    <div className="mb-3">
      <Map
        ref={mapRefCallback}
        attributionControl={provider.skipCustomAttribution ? undefined : { customAttribution: provider.attribution }}
        reuseMaps
        cursor={props.onClick ? "crosshair" : undefined}
        onZoomEnd={() => setUserInteracted(true)}
        onMoveEnd={() => setUserInteracted(true)}
        onClick={(e) => {
          const markerFeature = e.features?.find((f) => addressLayerIds.includes(f.layer.id));
          if (markerFeature?.properties?.id !== undefined) {
            props.onMarkerClick?.(markerFeature.properties.id);
            return;
          }
          if (!props.onClick) return;
          const { lng, lat } = e.lngLat;
          if (!isValidLatLng(lat, lng)) return;
          if (props.restrictMarkerToBounds && props.geojson) {
            const bounds = new LngLatBounds();
            extendBoundsWithGeometry(bounds, props.geojson as Geometry);
            const contains = bounds.contains(e.lngLat);
            if (!contains) return;
          }
          props.onClick([lng, lat]);
        }}
        onDblClick={(e) => {
          e.preventDefault();
          const features = mapRef.current?.getMap()?.queryRenderedFeatures(e.point, {
            layers: [TERRITORIES_FILL_LAYER_ID],
          });
          const id = features?.[0]?.properties?.id;
          if (id !== undefined) {
            props.navigateToMap(parseInt(id));
          }
        }}
        initialViewState={{
          bounds: mapBounds,
          fitBoundsOptions: { padding: { top: 30, bottom: 30, left: 30, right: 30 }, maxZoom: 18 },
        }}
        style={divStyle}
        mapStyle={tileStyle(provider)}
        validateStyle={false}
        transformRequest={transformMapboxRequest}
        interactiveLayerIds={interactiveLayerIds}
      >
        <NavigationControl showZoom={true} showCompass={false} visualizePitch={false} />
        {!!props.labeledLayers && props.labeledLayers.length > 0 && (
          <ConsolidatedLabeledLayers labeledLayers={props.labeledLayers} fontStack={fontStack} />
        )}
        {!!props.geojson && (
          <Source id="_boundaries" type="geojson" data={props.geojson as any}>
            <Layer id="_boundaries-fill" type="fill" paint={defaultGeoJSONPaint} />
            <Layer
              id="_boundaries-line"
              type="line"
              paint={{ "line-color": "rgba(51,136,256,0.6)", "line-width": 3 }}
            />
          </Source>
        )}
        {!!props.addresses && <AddressMarkerLayers addresses={props.addresses} congSettings={props.settings} />}
        {!!allAnnotations.features.length && (
          <Source id="_annotations" type="geojson" data={allAnnotations}>
            <Layer
              id="_annotations-line"
              type="line"
              filter={["==", ["geometry-type"], "LineString"]}
              paint={{ "line-color": "rgba(51,136,256,0.8)", "line-width": 3 }}
            />
            <Layer
              id="_annotations-text"
              filter={["==", ["geometry-type"], "Point"]}
              type="symbol"
              layout={{
                "text-field": annotationTextField,
                "text-variable-anchor": ["top", "bottom", "left", "right"],
                "text-justify": "auto",
                "text-padding": 2,
                "text-font": fontStack,
                "text-size": 13,
                "text-allow-overlap": true,
                "text-ignore-placement": false,
              }}
              paint={{
                "text-color": "#000000",
                "text-halo-color": "#F3C600",
                "text-halo-width": 2,
                "text-halo-blur": 1,
              }}
            />
          </Source>
        )}
      </Map>
    </div>
  );
}

// Memoize the map component to prevent re-renders when parent re-renders
// Only re-render when addresses array reference, geojson, or settings change
export const MapWithGeoJson = React.memo(MapWithGeoJsonInner, (prevProps, nextProps) => {
  // Return true to SKIP re-render, false to allow re-render
  return (
    prevProps.addresses === nextProps.addresses &&
    prevProps.geojson === nextProps.geojson &&
    prevProps.settings === nextProps.settings &&
    prevProps.territory?.id === nextProps.territory?.id &&
    prevProps.onMarkerClick === nextProps.onMarkerClick
  );
});

function AddressMarkerLayers(props: { addresses: TerritoryAddress[]; congSettings: CongSettings }) {
  const addressMarkerFontStack = mapFontStack(props.congSettings);

  const dnc = useMemo(() => addressesGeoJSON(props.addresses, (a: TerritoryAddress) => a.dnc), [props.addresses]);
  const addresses = useMemo(
    () => addressesGeoJSON(props.addresses, (a: TerritoryAddress) => !a.dnc),
    [props.addresses],
  );

  return (
    <>
      {!!addresses.features.length && (
        <Source id="addresses" type="geojson" data={addresses}>
          {/* Layer for markers with text - uses text-fit */}
          <Layer
            id="addresses-with-text"
            type="symbol"
            filter={["has", "text"]}
            layout={{
              "symbol-placement": "point",
              "icon-image": "regMarker",
              "icon-anchor": "bottom",
              "icon-size": 1.3,
              "icon-text-fit": "both",
              "icon-text-fit-padding": [2, 4, 2, 4],
              "text-field": ["get", "text"],
              "text-font": addressMarkerFontStack,
              "text-variable-anchor": ["bottom", "center", "top", "left", "right"],
              "text-variable-anchor-offset": ["bottom", [0, 0.5], "top", [0, -1]],
              "text-justify": "auto",
              "text-size": 12,
              "text-allow-overlap": true,
            }}
            paint={{ "text-color": "#FFFFFF" }}
          />
          {/* Layer for markers without text - no text-fit */}
          <Layer
            id="addresses-without-text"
            type="symbol"
            filter={["!", ["has", "text"]]}
            layout={{
              "symbol-placement": "point",
              "icon-image": "regMarker",
              "icon-anchor": "bottom",
              "icon-size": 0.5,
              "icon-text-fit": "none",
              "icon-allow-overlap": true,
            }}
          />
        </Source>
      )}
      {!!dnc.features.length && (
        <Source id="dnc" type="geojson" data={dnc}>
          {/* Layer for DNC markers with text - uses text-fit */}
          <Layer
            id="dnc-with-text"
            type="symbol"
            filter={["has", "text"]}
            layout={{
              "symbol-placement": "point",
              "icon-image": "dncMarker",
              "icon-anchor": "bottom",
              "icon-size": 1.3,
              "icon-text-fit": "both",
              "icon-text-fit-padding": [2, 4, 2, 4],
              "text-field": ["get", "text"],
              "text-font": addressMarkerFontStack,
              "text-variable-anchor": ["bottom", "center", "top", "left", "right"],
              "text-variable-anchor-offset": ["bottom", [0, 0.5], "top", [0, -1]],
              "text-justify": "auto",
              "text-size": 12,
              "text-allow-overlap": true,
            }}
            paint={{ "text-color": "#FFFFFF" }}
          />
          {/* Layer for DNC markers without text - no text-fit */}
          <Layer
            id="dnc-without-text"
            type="symbol"
            filter={["!", ["has", "text"]]}
            layout={{
              "symbol-placement": "point",
              "icon-image": "dncMarker",
              "icon-anchor": "bottom",
              "icon-size": 0.5,
              "icon-text-fit": "none",
              "icon-allow-overlap": true,
            }}
          />
        </Source>
      )}
    </>
  );
}

function ConsolidatedLabeledLayers(props: { labeledLayers: LabeledLayer[]; fontStack: string[] }) {
  const data = useMemo((): FeatureCollection => {
    const features: Feature[] = [];
    for (const ll of props.labeledLayers) {
      if (ll.geojson.type !== "Polygon" && ll.geojson.type !== "MultiPolygon") continue;
      features.push({
        type: "Feature",
        geometry: ll.geojson,
        properties: {
          id: ll.id,
          fillColor: ll.fillColor ?? "rgba(0,102,255,0.4)",
          strokeColor: ll.strokeColor ?? "rgba(0,102,255,0.6)",
        },
      });
      if (ll.center) {
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: ll.center },
          properties: { id: ll.id, label: ll.label },
        });
      }
    }
    return { type: "FeatureCollection", features };
  }, [props.labeledLayers]);

  return (
    <Source id="_territories" type="geojson" data={data}>
      <Layer
        id={TERRITORIES_FILL_LAYER_ID}
        type="fill"
        filter={["==", ["geometry-type"], "Polygon"]}
        paint={{ "fill-color": ["get", "fillColor"] }}
      />
      <Layer
        type="line"
        filter={["==", ["geometry-type"], "Polygon"]}
        paint={{ "line-color": ["get", "strokeColor"], "line-width": 3 }}
      />
      <Layer
        type="symbol"
        filter={["==", ["geometry-type"], "Point"]}
        layout={{
          "text-field": ["get", "label"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 10, 0, 11, 9, 14, 13, 17, 16],
          "text-anchor": "center",
          "text-justify": "center",
          "text-max-width": 8,
          "text-allow-overlap": false,
          "symbol-avoid-edges": true,
          "text-font": props.fontStack,
        }}
        paint={{
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        }}
      />
    </Source>
  );
}

export function addressIconUrl(ta: TerritoryAddress, excludeLabel = false) {
  if (!ta.location) {
    return "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="; //empty image
  }
  const color = ta.dnc ? "red" : "darkblue";
  const label = ta.dnc ? "X" : (ta.pinLabel ?? "");
  const letter = excludeLabel ? "" : label;
  //pin icon with letter and given color
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' fill='none'%3E%3Cpath fill='${color}' d='M15.23 30c-.205 0-.408-.038-.612-.115a1.835 1.835 0 0 1-.535-.305c-3.72-3.287-6.497-6.338-8.331-9.154C3.917 17.61 3 14.98 3 12.535c0-3.822 1.23-6.866 3.689-9.134C9.148 1.134 11.995 0 15.229 0c3.236 0 6.083 1.134 8.543 3.401 2.459 2.268 3.688 5.312 3.687 9.134 0 2.446-.918 5.077-2.752 7.892-1.834 2.816-4.611 5.867-8.331 9.153a1.83 1.83 0 0 1-.535.305 1.732 1.732 0 0 1-.612.115Z' /%3E%3Ctext x='50%25' y='50%25' fill='white' dominant-baseline='middle' font-family='Arial' font-size='20' text-anchor='middle'%3E${letter}%3C/text%3E%3C/svg%3E`;
}

enum MapMarkerImage {
  Regular = "regMarker",
  DNC = "dncMarker",
}

export function addMarkerImages(map: MaplibreMap) {
  const regTerritoryAddress: TerritoryAddress = {
    id: 0,
    territoryId: 0,
    sortOrder: defaultSortOrder,
    line1: "",
    dnc: false,
    hideOnMap: false,
    tags: [],
    location: { x: 1, y: 1, srid: 1 }, // we just need a location object so that addressIconUrl will return the marker image
  };

  if (!map) return;
  if (!map.hasImage(MapMarkerImage.Regular)) {
    const img = new Image(36, 36);
    img.onload = () => {
      map.addImage(MapMarkerImage.Regular, img);
    };
    img.src = addressIconUrl(regTerritoryAddress, true);
  }
  if (!map.hasImage(MapMarkerImage.DNC)) {
    const img = new Image(36, 36);
    img.onload = () => {
      map.addImage(MapMarkerImage.DNC, img);
    };
    img.src = addressIconUrl({ ...regTerritoryAddress, dnc: true }, true);
  }
}
